import { Injectable } from '@angular/core';
import * as moment from 'moment';
import * as momentTimeZone from 'moment-timezone';
import { BehaviorSubject, Observable, throwError, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { RpcError } from '../../shared/error/rpc.error';
import { RpcService } from '../../shared/http/rpc.service';
import { ValuePair } from '../../shared/interfaces/ValuePair';
import { Country } from '../const/country';
import { CompanySettingsDto } from '../dto/company-settings.dto';
import { CompanySettings } from '../model/company-settings';
import { ServerService } from 'app/shared/server/server.service';
import { Version } from 'app/shared/version/version';
import { environment } from 'environments/environment';
import { TokenService } from 'app/authentication/token.service';
import { VoidReason } from '../model/void-reason';

const VOID_REASON_VERSION_SERVER = new Version(7, 19, 0);
@Injectable()
export class CompanySettingsService {
  public voidReasonsEnabled$: Observable<boolean>;
  private companySettingsChanged: BehaviorSubject<
    boolean
  > = new BehaviorSubject(true);
  private voidReasonsSubject: Subject<VoidReason[]> = new Subject();
  private voidReasosnsEnabledSubject: BehaviorSubject<
    boolean
  > = new BehaviorSubject(false);

  constructor(
    private rpc: RpcService,
    server: ServerService,
    tokenService: TokenService
  ) {
    this.voidReasonsEnabled$ = this.voidReasosnsEnabledSubject.asObservable();
    tokenService.tokenChanged$
      .pipe(
        switchMap(token => {
          if (!token) {
            return of(false);
          }
          return server
            .hasAllowedServerVersion(VOID_REASON_VERSION_SERVER)
            .pipe(
              map(
                hasCorrectServerVersion =>
                  hasCorrectServerVersion && environment.voidReasons
              )
            );
        })
      )
      .subscribe(enabled => this.voidReasosnsEnabledSubject.next(enabled));
  }

  public get companySettingsChanged$(): Observable<boolean> {
    return this.companySettingsChanged.asObservable();
  }

  public getCompanySettings(): Observable<CompanySettings> {
    return this.rpc.makeManagerCall('getCompany').pipe(
      map(CompanySettings.fromDto),
      tap((companySettings: CompanySettings) => {
        this.setTimezone(companySettings.timeZoneId);
        this.setLocale(companySettings.locale);
        this.companySettingsChanged.next(true);
      })
    );
  }

  public saveCompanySettings(
    settings: CompanySettings
  ): Observable<CompanySettings> {
    const toSave: CompanySettings = Object.assign(
      new CompanySettings(),
      settings
    );
    if (
      Country.isCountry('Belgium', toSave.country) &&
      toSave.establishmentUnitNumber !== undefined
    ) {
      toSave.secondaryVatNumber = toSave.establishmentUnitNumber || '';
    }
    return this.saveCompanySettingsRpc(CompanySettings.toDTO(toSave));
  }

  public getTimeZones(): Observable<Array<ValuePair>> {
    return this.rpc.makeManagerCall('getAvailableTimeZones').pipe(
      map((timeZones: Array<TimeZone>) => {
        return timeZones.map((timeZone: TimeZone) => {
          return {
            value: timeZone.timeZone,
            name: this.formatTimeZone(timeZone.timeZone)
          };
        });
      })
    );
  }
  public getTimeZoneIds(id: number): Observable<Array<ValuePair>> {
    id = id === null ? 0 : id;
    return this.rpc.makeManagerCall('getAvailableTimeZoneIDs', [id]).pipe(
      map((timeZoneIds: Array<string>) => {
        return timeZoneIds.map((timeZoneId: string) => {
          return {
            value: timeZoneId,
            name: timeZoneId
          };
        });
      })
    );
  }

  public resetStock(): Observable<boolean> {
    return this.rpc.makeManagerCall('resetAllProductStock');
  }

  public getKeyBoardLetters(): Observable<Array<string>> {
    return this.rpc.makeManagerCall('getKeyboardLetters');
  }

  public setKeyBoardLetters(keyBoardLetters: Array<string>): Observable<void> {
    return this.rpc.makeManagerCall('setKeyboardLetters', [keyBoardLetters]);
  }

  public getVoidReasons(): Observable<VoidReason[]> {
    setTimeout(() => {
      this.voidReasonsEnabled$
        .pipe(
          switchMap(voidReasonsEnabled => {
            if (voidReasonsEnabled) {
              return this.rpc
                .makeManagerCall('getVoidReasons')
                .pipe(catchError(() => of([])));
            }
            return of([]);
          })
        )
        .subscribe(reasons => {
          this.voidReasonsSubject.next(reasons);
        });
    });
    return this.voidReasonsSubject.asObservable();
  }

  public saveVoidReasons(voidReasons: VoidReason[]): Observable<VoidReason[]> {
    return this.voidReasonsEnabled$.pipe(
      switchMap(voidReasonsEnabled => {
        if (voidReasonsEnabled) {
          return this.rpc
            .makeManagerCall('saveVoidReasons', [voidReasons])
            .pipe(tap(reasons => this.voidReasonsSubject.next(reasons)));
        }
        return of([]);
      })
    );
  }

  public sendTestEmail(): Observable<boolean> {
    return this.rpc.makeManagerCall('sendTestEmail').pipe(
      map(() => true),
      catchError((error: RpcError) => {
        return throwError(error);
      })
    );
  }
  private formatTimeZone(timeZone: number): string {
    const absoluteHour: number = Math.abs(timeZone);
    const spillOver: number = (absoluteHour - Math.floor(absoluteHour)) * 60;

    const hour: string =
      absoluteHour < 10
        ? `0${Math.floor(absoluteHour)}`
        : Math.floor(absoluteHour).toString();
    const minutes: string =
      spillOver.toString().length < 2 ? `${spillOver}0` : spillOver.toString();

    return `GMT ${timeZone < 0 ? '-' : '+'}${hour}:${minutes}`;
  }

  private setTimezone(timezone: string): void {
    //noinspection TypeScriptUnresolvedVariable
    momentTimeZone.tz.setDefault(timezone);
  }

  private setLocale(locale: string): void {
    moment.locale(locale);
  }

  private saveCompanySettingsRpc(
    settings: CompanySettingsDto
  ): Observable<CompanySettings> {
    return this.rpc.makeManagerCall('setCompany', [settings]).pipe(
      switchMap(() => {
        return this.getCompanySettings();
      })
    );
  }
}

export interface TimeZone {
  info: string;
  timeZone: number;
}
